import React, { useEffect, useState } from "react";
import taskApi from "../../api/taskApi";
import promosApi from "../../api/promosApi";
import tokenApi from "../../api/tokenApi";
import getIcon from "../../utils/Icon";
import { Input, Select, Tooltip, message, Spin, Pagination } from "antd";
import { ClockCircleOutlined, LinkOutlined, CopyOutlined, SearchOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { ListOrdered } from "lucide-react";
import "../../components/AddUserModal/style.css";

const { Option } = Select;

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const statusTone = {
    PENDING: { border: "border-amber-400/35", bg: "bg-amber-500/15", text: "text-amber-100" },
    ACTIVE: { border: "border-emerald-400/35", bg: "bg-emerald-500/15", text: "text-emerald-100" },
    COMPLETED: { border: "border-sky-400/35", bg: "bg-sky-500/15", text: "text-sky-100" },
    "в ожидании": { border: "border-amber-400/35", bg: "bg-amber-500/15", text: "text-amber-100" },
    активен: { border: "border-emerald-400/35", bg: "bg-emerald-500/15", text: "text-emerald-100" },
    завершён: { border: "border-sky-400/35", bg: "bg-sky-500/15", text: "text-sky-100" },
};

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

function StatusPill({ status }) {
    const tone = statusTone[status] || {
        border: "border-white/15",
        bg: "bg-white/[0.08]",
        text: "text-white/80",
    };
    return (
        <span
            className={cn(
                "inline-flex shrink-0 rounded-full border px-3 py-1 text-[12px] font-semibold capitalize",
                tone.border,
                tone.bg,
                tone.text
            )}
        >
            {status}
        </span>
    );
}

export default function Tasks() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [promosWithPromoters, setPromosWithPromoters] = useState({});
    const [tokensInfo, setTokensInfo] = useState({});
    const [search, setSearch] = useState("");
    const [tasksPerPage, setTasksPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!search) return setFilteredData(data);

        const filtered = data
            .map((group) => ({
                ...group,
                tasks: group.tasks.filter((task) => task.title.toLowerCase().includes(search.toLowerCase())),
            }))
            .filter((g) => g.tasks.length > 0);

        setFilteredData(filtered);
        setCurrentPage(1);
    }, [search, data]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await taskApi.getAllTasks();
            setData(response);
            setFilteredData(response);

            const promotersMap = {};
            const tokensMap = {};
            await Promise.all(
                response.map(async ({ promo }) => {
                    try {
                        const fullPromo = await promosApi.promosApi.getById(promo.promo_id);
                        if (fullPromo.promoters && fullPromo.promoters.length > 0) {
                            promotersMap[promo.promo_id] = fullPromo.promoters[0];
                        }

                        if (promo.tokenType && promo.tokenType.length > 10) {
                            try {
                                const tokenData = await tokenApi.getTokenByAddress(promo.tokenType);
                                if (tokenData) {
                                    tokensMap[promo.promo_id] = tokenData;
                                }
                            } catch (tokenError) {
                                console.error(`Ошибка при загрузке токена для промо ${promo.promo_id}:`, tokenError);
                            }
                        }
                    } catch (e) {
                        console.error(`Ошибка при загрузке промо ${promo.promo_id}:`, e);
                    }
                })
            );
            setPromosWithPromoters(promotersMap);
            setTokensInfo(tokensMap);
        } catch (e) {
            console.error("Ошибка при загрузке:", e);
            message.error("Не удалось загрузить задачи");
        } finally {
            setLoading(false);
        }
    };

    const isValidURL = (str) => {
        try {
            new URL(str);
            return true;
        } catch {
            return false;
        }
    };

    const copyTokenAddress = (address) => {
        navigator.clipboard.writeText(address);
        message.success("Адрес токена скопирован!");
    };

    const totalPages = Math.max(1, Math.ceil(filteredData.length / tasksPerPage) || 1);
    const paginatedData = filteredData.slice((currentPage - 1) * tasksPerPage, currentPage * tasksPerPage);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const taskGroupsTotal = filteredData.length;
    const tasksCount = filteredData.reduce((acc, g) => acc + (g.tasks?.length || 0), 0);

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-20 -top-20 h-40 w-40 rounded-full bg-white/5 blur-2xl" />
                <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <ListOrdered className="h-5 w-5 text-white/85" />
                        </span>
                        <div className="min-w-0">
                            <h1 className="text-lg font-semibold tracking-tight text-white/90 sm:text-xl">Tasks</h1>
                            <p className="text-[12px] text-white/45">
                                {taskGroupsTotal} промо · {tasksCount} задач
                                {search ? " · отфильтровано" : ""}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                        <div className="flex w-full min-w-[200px] max-w-md flex-col gap-1">
                            <span className="text-[11px] font-medium uppercase tracking-wide text-white/40">Поиск</span>
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                allowClear
                                prefix={<SearchOutlined className="text-white/35" />}
                                placeholder="По названию задачи..."
                                className="!rounded-xl !border-white/10 !bg-white/[0.06] py-2 text-white/90 placeholder:!text-white/35 hover:!border-white/[0.18] focus:!border-emerald-400/50"
                            />
                        </div>
                        <div className="flex w-full flex-col gap-1 sm:w-44">
                            <span className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                                Промо на странице
                            </span>
                            <Select
                                value={tasksPerPage}
                                onChange={(val) => {
                                    setTasksPerPage(val);
                                    setCurrentPage(1);
                                }}
                                popupClassName="glass-ant-select-dropdown"
                                className="w-full [&_.ant-select-selector]:!min-h-[42px] [&_.ant-select-selector]:!rounded-xl [&_.ant-select-selector]:!border-white/10 [&_.ant-select-selector]:!bg-white/[0.06] [&_.ant-select-selection-item]:!text-white/90 [&_.ant-select-arrow]:!text-white/45"
                            >
                                {[10, 20, 30, 40].map((n) => (
                                    <Option key={n} value={n}>
                                        {n} промо
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                </div>
            </section>

            <div
                className={cn(
                    shellClass,
                    "flex shrink-0 flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                )}
            >
                <span className="text-[13px] text-white/50">
                    Страница {currentPage} из {totalPages}
                </span>
                <Pagination
                    current={currentPage}
                    pageSize={tasksPerPage}
                    total={filteredData.length}
                    onChange={(page) => setCurrentPage(page)}
                    showSizeChanger={false}
                    showLessItems
                    responsive
                    showTotal={(total, range) => `${range[0]}-${range[1]} из ${total} промо`}
                    className="pagination-custom !mb-0 flex flex-wrap justify-end"
                    size="small"
                />
            </div>

            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col")}>
                {loading ? (
                    <div className="flex flex-1 items-center justify-center py-24">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                ) : paginatedData.length === 0 ? (
                    <p className="py-16 text-center text-sm text-white/45">Задачи не найдены</p>
                ) : (
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 sm:p-5">
                        <div className="space-y-4">
                            {paginatedData.map(({ promo, tasks }, index) => {
                                const promoter = promosWithPromoters[promo.promo_id];
                                const tokenInfo = tokensInfo[promo.promo_id];

                                return (
                                    <motion.div
                                        key={promo.promo_id || promo.id}
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.25, delay: index * 0.04 }}
                                        className="rounded-xl border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-white/[0.14] hover:bg-white/[0.05]"
                                    >
                                        <div className="mb-4 flex flex-col gap-3 border-b border-white/10 pb-4 sm:flex-row sm:items-start sm:justify-between">
                                            <div className="min-w-0 flex-1">
                                                <div className="mb-3 flex flex-wrap items-center gap-3">
                                                    {promoter ? (
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            {promoter.logo && (
                                                                <img
                                                                    src={`https://api.duotetra.quest${promoter.logo}`}
                                                                    alt=""
                                                                    className="h-9 w-9 rounded-full border border-white/15 object-cover"
                                                                    onError={(e) => {
                                                                        e.target.style.display = "none";
                                                                    }}
                                                                />
                                                            )}
                                                            <span className="text-[15px] font-semibold text-white/90">
                                                                {promoter.name || "Промоутер"}
                                                            </span>
                                                            {promoter.link && (
                                                                <a
                                                                    href={promoter.link}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="ml-1 flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-white/[0.05] opacity-90 transition hover:border-white/20 hover:bg-white/[0.1]"
                                                                    title="Telegram"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <img src={getIcon("telegram")} alt="" className="h-4 w-4" />
                                                                </a>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-[15px] font-semibold text-white/75">
                                                            {promo.title || "Без названия"}
                                                        </span>
                                                    )}

                                                    {tokenInfo ? (
                                                        <Tooltip
                                                            title={
                                                                <div className="max-w-xs text-xs text-white">
                                                                    <div className="mb-2 font-bold">
                                                                        {tokenInfo.name} ({tokenInfo.symbol?.toUpperCase()})
                                                                    </div>
                                                                    <div className="mb-2 text-white/70">
                                                                        Blockchain: {tokenInfo.blockchain || "Unknown"}
                                                                    </div>
                                                                    <div className="mb-2 break-all text-[10px] text-white/55">
                                                                        {tokenInfo.address}
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            copyTokenAddress(tokenInfo.address);
                                                                        }}
                                                                        className="mt-2 inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200"
                                                                    >
                                                                        <CopyOutlined /> Копировать адрес
                                                                    </button>
                                                                </div>
                                                            }
                                                            placement="bottom"
                                                        >
                                                            <div className="flex cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-white/[0.06] px-2 py-1 transition hover:border-white/18">
                                                                {tokenInfo.logoUrl && (
                                                                    <img
                                                                        src={tokenInfo.logoUrl}
                                                                        alt=""
                                                                        className="h-5 w-5 rounded-full object-cover"
                                                                        onError={(e) => {
                                                                            e.target.style.display = "none";
                                                                        }}
                                                                    />
                                                                )}
                                                                <span className="text-[13px] font-medium text-white/75">
                                                                    {tokenInfo.name ||
                                                                        tokenInfo.symbol?.toUpperCase() ||
                                                                        "Token"}
                                                                </span>
                                                            </div>
                                                        </Tooltip>
                                                    ) : (
                                                        <span className="rounded-lg border border-white/10 bg-white/[0.05] px-2 py-1 font-mono text-[12px] text-white/50">
                                                            {promo.tokenType?.slice(0, 6)}…{promo.tokenType?.slice(-4)}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-white/50">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <img src={getIcon("cup")} alt="" className="h-4 w-4 opacity-80" />
                                                        Призовых мест:{" "}
                                                        <span className="font-medium text-white/90">{promo.prizePlacesCount}</span>
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        {tokenInfo?.logoUrl ? (
                                                            <img
                                                                src={tokenInfo.logoUrl}
                                                                alt=""
                                                                className="h-4 w-4 rounded-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.style.display = "none";
                                                                }}
                                                            />
                                                        ) : (
                                                            <span>💰</span>
                                                        )}
                                                        Сумма:{" "}
                                                        <span className="font-medium text-white/90">{promo.totalAmount}</span>
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <img src={getIcon("gift")} alt="" className="h-4 w-4 opacity-80" />
                                                        Победителей:{" "}
                                                        <span className="font-medium text-white/90">{promo.winnersCount}</span>
                                                    </span>
                                                </div>
                                            </div>

                                            <StatusPill status={promo.status} />
                                        </div>

                                        <div className="space-y-2">
                                            {tasks.map((task, i) => (
                                                <motion.div
                                                    key={task.id}
                                                    initial={{ opacity: 0, x: -8 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.18, delay: i * 0.02 }}
                                                    className="flex flex-col gap-2 rounded-xl border border-white/[0.07] bg-white/[0.03] p-3 transition-colors hover:border-white/12 hover:bg-white/[0.05] sm:flex-row sm:items-center sm:justify-between"
                                                >
                                                    <div className="flex min-w-0 items-center gap-3">
                                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06]">
                                                            <img src={getIcon(task.title)} alt="" className="h-5 w-5" />
                                                        </div>
                                                        <span className="truncate text-[13px] font-medium text-white/90">
                                                            {task.title}
                                                        </span>
                                                    </div>

                                                    <div className="flex shrink-0 items-center gap-3 sm:justify-end">
                                                        <span className="inline-flex items-center gap-1 text-[12px] text-white/45">
                                                            <ClockCircleOutlined />
                                                            {new Date(task.created_at).toLocaleDateString("ru-RU")}
                                                        </span>
                                                        {isValidURL(task.details) && (
                                                            <Tooltip title="Открыть задачу">
                                                                <a
                                                                    href={task.details}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-400/25 bg-emerald-500/10 text-emerald-300 transition hover:border-emerald-400/45 hover:bg-emerald-500/20"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <LinkOutlined />
                                                                </a>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
